const num = v => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * One FCR market's per-market figures off the wire, or null when the backend does not send
 * them yet (rows settled before the per-market projection deployed) or the row's blob has no
 * such market. Every field is nullable: null is "not measured", which is not the same as a
 * real zero (a market never activated, a drained reservoir).
 */
const marketDetail = d =>
    d === null || d === undefined
        ? null
        : {
              powerFollowingScore: num(d.power_following_score),
              powerFollowingDeviation: num(d.power_following_deviation),
              enduranceMeanH: num(d.endurance_mean_h),
              enduranceMinH: num(d.endurance_min_h),
              fcrActivatedDuration: num(d.fcr_activated_duration),
          };

/**
 * One `GET /batteries/:id/delivered` row → the camelCased `Delivered` shape the pure modules
 * read. Units are preserved (MW / MWh / s); the ×1000 to kW happens once, at the chart and
 * the tiles, so the summary maths stays in the wire's own unit.
 *
 * @param {object} row
 * @returns {import('./marketReportsV1').Delivered}
 */
/** The flat `markets` projection off the wire (battery and device rows share the shape). */
export const marketsFromWire = m =>
    m === null || m === undefined
        ? null
        : {
              availablePowerMean: num(m.available_power_mean),
              availablePowerMax: num(m.available_power_max),
              availablePowerMin: num(m.available_power_min),
              fcrNScheduledPower: num(m.fcr_n_scheduled_power) ?? 0,
              fcrDUpScheduledPower: num(m.fcr_d_up_scheduled_power) ?? 0,
              fcrDDownScheduledPower: num(m.fcr_d_down_scheduled_power) ?? 0,
              intradayScheduledPower: num(m.intraday_scheduled_power) ?? 0,
              committedPower: num(m.committed_power) ?? 0,
              fcrDUp: marketDetail(m.fcr_d_up),
              fcrDDown: marketDetail(m.fcr_d_down),
              fcrN: marketDetail(m.fcr_n),
          };

export const deliveredFromWire = row => {
    return {
        start: row.start,
        end: row.end,
        totalEnergy: row.total_energy,
        averagePower: row.average_power,
        maxPower: row.max_power,
        minPower: row.min_power,
        activeDuration: row.active_duration,
        setpointFollowingScore: row.setpoint_following_score,
        setpointFollowingDelay: row.setpoint_following_delay,
        deviceCount: row.device_count,
        disabledDeviceCount: row.disabled_device_count,
        measurement: row.measurement,
        enabledNameplateMw: num(row.enabled_nameplate_mw),
        markets: marketsFromWire(row.markets),
    };
};

/**
 * The three-way measurement of one device row. The device wire carries no `measurement`
 * field; it is derived: a row computed from zero samples is a monitoring hole (`unmeasured` —
 * every figure on it is a fallthrough zero, not a reading), a disabled device sat out
 * (`out_of_service`), everything else is a reading. Legacy rows without a sample count read
 * as measured — they were only written off real samples.
 */
export const deviceMeasurement = row => {
    if (row.num_samples === 0) {
        return 'unmeasured';
    }
    if (row.enabled === false) {
        return 'out_of_service';
    }
    return 'measured';
};

/**
 * One `GET /devices/:id/delivery-reports` row → the same camelCased `Delivered` shape the
 * battery modules read, so one set of chart machinery serves both pages. The device's own
 * figures stay in the wire's units (kW / kWh); everything the charts read — the `markets`
 * projection — is MW, exactly like the battery wire. The member counts are synthesized from
 * the derived measurement (a device is its own only member).
 *
 * @returns {import('./marketReportsV1').Delivered}
 */
export const deviceDeliveredFromWire = row => {
    const measurement = deviceMeasurement(row);
    return {
        start: row.start,
        end: row.end,
        totalEnergy: row.total_energy,
        averagePower: row.average_power,
        maxPower: row.max_power,
        minPower: row.min_power,
        activeDuration: row.active_duration,
        setpointFollowingScore: row.setpoint_following_score,
        setpointFollowingDelay: row.setpoint_following_delay,
        deviceCount: measurement === 'measured' ? 1 : 0,
        disabledDeviceCount: measurement === 'out_of_service' ? 1 : 0,
        measurement,
        enabledNameplateMw: null,
        markets: marketsFromWire(row.markets),
    };
};

/** MW → kW with one decimal, the commitment tiles' unit. */
export const fmtKw = mw => (mw * 1000).toFixed(1);
